import React, { useState, useEffect, useMemo } from 'react';
import { motion as Motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSelector } from 'react-redux';
import CommonLoader from '../CommonLoader';
import { useTranslation } from 'react-i18next';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { doctorCurrencySymbol } from '../../utils/currency';

const TOTAL_KEY = 'Total';
const TOTAL_COLOUR = '#4ABDBD';

// Per-chip translated specialty label for the toggleable legend.
const SpecialtyName = ({ specialty }) => {
  const label = useMasterLabel('masterSpecialty', specialty?.specialtyId, 'name', specialty?.specialtyName || '');
  return <span data-testid={`specialty-trend-legend-text-${specialty?.specialtyId || specialty?.specialtyName}`}>{label}</span>;
};

const SpecialtyTrendGraph = ({ selectedPeriod, trendOverTime, loading }) => {
  const { t } = useTranslation('dashboard');

  const { selectedDoctor } = useSelector((state) => state.doctor);

  // The doctor's currency, from their country's currency code (utils/currency.js).
  const getCurrencySymbol = () => doctorCurrencySymbol(selectedDoctor);

  const [metric, setMetric] = useState('appointments');
  const [visibleLines, setVisibleLines] = useState({});

  const specialties = useMemo(() => trendOverTime?.specialties || [], [trendOverTime]);
  const series = useMemo(
    () => (metric === 'revenue' ? trendOverTime?.revenueSeries : trendOverTime?.appointmentSeries) || [],
    [metric, trendOverTime]
  );

  const isRevenue = metric === 'revenue';
  const currencySymbol = getCurrencySymbol();

  // Legend keys: Total first, then each specialty name snapshot.
  const legendKeys = useMemo(
    () => [TOTAL_KEY, ...specialties.map((s) => s?.specialtyName).filter(Boolean)],
    [specialties]
  );

  // Default every line to visible whenever the available keys change.
  useEffect(() => {
    setVisibleLines((prev) => {
      const next = {};
      legendKeys.forEach((k) => {
        next[k] = prev[k] === undefined ? true : prev[k];
      });
      return next;
    });
  }, [legendKeys]);

  const isVisible = (key) => visibleLines[key] !== false;
  const toggleLine = (key) => setVisibleLines((prev) => ({ ...prev, [key]: prev[key] === false }));

  const colourFor = (key) => {
    if (key === TOTAL_KEY) return TOTAL_COLOUR;
    const match = specialties.find((s) => s?.specialtyName === key);
    return match?.colour || '#9CA3AF';
  };

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    if (num >= 1000000) return `${currencySymbol}${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${currencySymbol}${(num / 1000).toFixed(1)}k`;
    return `${currencySymbol}${num.toLocaleString()}`;
  };

  const formatYAxis = (value) => (isRevenue ? formatCurrency(value) : (Number(value) || 0).toLocaleString());

  const formatValue = (value) =>
    isRevenue ? `${currencySymbol}${(Number(value) || 0).toLocaleString()}` : (Number(value) || 0).toLocaleString();

  // Sum of the Total series across all buckets for the active metric.
  const totalSum = useMemo(
    () => series.reduce((acc, bucket) => acc + (Number(bucket?.Total) || 0), 0),
    [series]
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div data-testid="specialty-trend-tooltip" className="bg-white p-2 border border-gray-200 rounded shadow-lg">
        <p data-testid="specialty-trend-tooltip-label" className="font-semibold text-gray-900 mb-1">{label}</p>
        {payload.map((entry) => (
          <p
            key={entry?.dataKey}
            data-testid={`specialty-trend-tooltip-row-${entry?.dataKey}`}
            className="text-xs text-gray-700 flex items-center gap-1"
          >
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: entry?.color }} />
            <span className="capitalize">{entry?.name}:</span>
            <span className="font-semibold">{formatValue(entry?.value)}</span>
          </p>
        ))}
      </div>
    );
  };

  const hasData = series.length > 0 && specialties.length > 0;

  return (
    <Motion.div
      data-testid="specialty-trend-graph"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md"
    >
      <div data-testid="specialty-trend-header" className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 data-testid="specialty-trend-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
          {t('dashboard.trends_over_time', 'Trends over time')}
        </h3>

        <div
          data-testid="specialty-trend-metric-toggle"
          className="inline-flex w-fit rounded-lg border border-gray-200/50 bg-gray-50 p-0.5 shadow-sm"
        >
          <button
            type="button"
            data-testid="specialty-trend-metric-appointments"
            onClick={() => setMetric('appointments')}
            className={`px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-md transition-all duration-200 ${
              !isRevenue ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white shadow' : 'text-gray-600 hover:text-[#4ABDBD]'
            }`}
          >
            {t('dashboard.appointments_label', 'Appointments')}
          </button>
          <button
            type="button"
            data-testid="specialty-trend-metric-revenue"
            onClick={() => setMetric('revenue')}
            className={`px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-md transition-all duration-200 ${
              isRevenue ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white shadow' : 'text-gray-600 hover:text-[#4ABDBD]'
            }`}
          >
            {t('dashboard.revenue', 'Revenue')}
          </button>
        </div>
      </div>

      {loading ? (
        <div data-testid="specialty-trend-loading" className="flex flex-col items-center justify-center py-12">
          <CommonLoader />
          <span className="text-sm text-gray-600 mt-2">{t('dashboard.loading', 'Loading...')}</span>
        </div>
      ) : !hasData ? (
        <div data-testid="specialty-trend-empty" className="h-72 sm:h-80 flex flex-col items-center justify-center">
          <TrendingUp className="w-10 h-10 text-gray-300 mb-2" />
          <p className="text-sm text-gray-600">{t('dashboard.no_trend_data', 'No trend data')}</p>
        </div>
      ) : (
        <Motion.div
          key={`${metric}-${selectedPeriod || ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          <div
            data-testid="specialty-trend-total"
            className="flex items-center justify-between p-3 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3A9B9B]/5 rounded-lg"
          >
            <span className="text-sm font-medium text-gray-600">
              {isRevenue ? t('dashboard.total_revenue_label', 'Total Revenue') : t('dashboard.total_appointments', 'Total Appointments')}
            </span>
            <span data-testid="specialty-trend-total-value" className="text-lg sm:text-xl font-bold text-gray-900">
              {isRevenue ? formatValue(totalSum) : totalSum.toLocaleString()}
            </span>
          </div>

          <div data-testid="specialty-trend-legend" className="flex flex-wrap gap-2">
            {legendKeys.map((key) => {
              const active = isVisible(key);
              const colour = colourFor(key);
              const specialty = key === TOTAL_KEY ? null : specialties.find((s) => s?.specialtyName === key);
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`specialty-trend-legend-chip-${key}`}
                  onClick={() => toggleLine(key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all duration-200 ${
                    active ? 'border-gray-200 bg-white text-gray-700 shadow-sm' : 'border-gray-200/50 bg-gray-50 text-gray-400 line-through'
                  }`}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: active ? colour : '#D1D5DB' }}
                  />
                  {key === TOTAL_KEY ? (
                    <span>{t('dashboard.total', 'Total')}</span>
                  ) : (
                    <span className="capitalize">
                      <SpecialtyName specialty={specialty || { specialtyName: key }} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div data-testid="specialty-trend-chart" className="h-72 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" fontSize={11} tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} />
                <YAxis fontSize={11} tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={formatYAxis} />
                <Tooltip content={<CustomTooltip />} />
                {isVisible(TOTAL_KEY) && (
                  <Line
                    type="monotone"
                    dataKey={TOTAL_KEY}
                    name={t('dashboard.total', 'Total')}
                    stroke={TOTAL_COLOUR}
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                )}
                {specialties
                  .filter((s) => s?.specialtyName && isVisible(s.specialtyName))
                  .map((s) => (
                    <Line
                      key={s.specialtyId || s.specialtyName}
                      type="monotone"
                      dataKey={s.specialtyName}
                      name={s.specialtyName}
                      stroke={s.colour || '#9CA3AF'}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3 }}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Motion.div>
      )}
    </Motion.div>
  );
};

export default SpecialtyTrendGraph;
